import React, { useState, useEffect } from 'react';
import {
  LineChart, Area, Line, CartesianGrid, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ComposedChart, Bar
} from 'recharts';
import { DateTime } from 'luxon';
import { exportMetricsToCsv } from "../../utils";

interface DataItem {
  date: string;
  treatment: { total: number };
  mobility: { average: number };
  pain: { average: number };
  recovery: number;
}

interface MetricsGraphProps {
  userData: DataItem[];
  groupData: DataItem[];
  userName: string | undefined;
  groupId: string | null | undefined;
  userEmail: string | null | undefined;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any;
}

interface ReadoutData {
  treatment: { hours: number | null; minutes: number | null };
  mobility: { value: number; trend: number };
  pain: { value: number; trend: number };
  recovery: { value: number; trend: number };
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <div className="custom-tooltip" style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc', fontSize: '12px' }}>
        <p>Date: {data.date}</p>
        <p>Treatment: {data.treatmentTotal} minutes</p>
        <p>Mobility: {Math.round(data.mobilityAverage)}</p>
        <p>Recovery: {Math.round(data.recovery)}</p>
        <p>Pain: {Math.round(data.painAverage)}</p>
      </div>
    );
  }
  return null;
};

const initialLineVisibility = [true, true, true, true];
const timeRanges = [
  { label: '7 Days', value: 7 },
  { label: '1 Month', value: 30 },
  { label: '3 Months', value: 90 },
  { label: 'All Time', value: Infinity },
];

const MetricsGraph: React.FC<MetricsGraphProps> = ({ userData, groupData, userName, groupId, userEmail }) => {
  const [lineVisibility, setLineVisibility] = useState<boolean[]>(initialLineVisibility);
  const [daysShown, setDaysShown] = useState<number>(7);
  const [filteredData, setFilteredData] = useState<DataItem[]>([]);
  const [useFakeData, setUseFakeData] = useState<boolean>(false);
  const [useGroupData, setUseGroupData] = useState<boolean>(false);
  const [readoutData, setReadoutData] = useState<ReadoutData | null>(null);

  useEffect(() => {
    const sourceData = useGroupData ? groupData : userData;

    const now = DateTime.now();
    const data = daysShown === Infinity
      ? sourceData
      : sourceData.filter(item => {
          const itemDate = DateTime.fromFormat(item.date, 'dd/MM/yyyy');
          return itemDate >= now.minus({ days: daysShown });
        });

    setFilteredData(data);

    let totalTreatment = 0, totalMobility = 0, totalRecovery = 0, totalPain = 0;
    let mobilityTrend = 0, recoveryTrend = 0, painTrend = 0;

    data.forEach((item, index) => {
      totalTreatment += item.treatment.total;
      totalMobility += item.mobility.average;
      totalRecovery += item.recovery;
      totalPain += item.pain.average;

      if (index > 0) {
        mobilityTrend += item.mobility.average - data[index - 1].mobility.average;
        recoveryTrend += item.recovery - data[index - 1].recovery;
        painTrend += item.pain.average - data[index - 1].pain.average;
      }
    });

    const length = data.length;
    
    setReadoutData({
      treatment: {
        hours: Math.floor(totalTreatment / 60),
        minutes: Math.round(totalTreatment % 60 * 100) / 100, // Rounded to 2 decimal places
      },
      mobility: {
        value: length ? parseFloat((totalMobility / length).toFixed(2)) : 0,
        trend: parseFloat(mobilityTrend.toFixed(2)),
      },
      recovery: {
        value: length ? parseFloat((totalRecovery / length).toFixed(2)) : 0,
        trend: parseFloat(recoveryTrend.toFixed(2)),
      },
      pain: {
        value: length ? parseFloat((totalPain / length).toFixed(2)) : 0,
        trend: parseFloat(painTrend.toFixed(2)),
      },
    });
  }, [userData, groupData, daysShown, useFakeData, useGroupData]);

  const toggleLineVisibility = (index: number) => {
    setLineVisibility(prev => {
      const newVisibility = [...prev];
      newVisibility[index] = !newVisibility[index];
      return newVisibility;
    });
  };

  const formatTick = (tick: number) => {
    if (daysShown === 7) return DateTime.fromMillis(tick).toFormat('EEE');
    if (daysShown === 30) return DateTime.fromMillis(tick).toFormat('dd');
    if (daysShown === 90) return DateTime.fromMillis(tick).toFormat('MMM dd');
    return DateTime.fromMillis(tick).toFormat('dd/MM/yyyy');
  };

  const transformedData = filteredData.map(item => ({
    ...item,
    treatmentTotal: item.treatment.total,
    mobilityAverage: item.mobility.average,
    painAverage: item.pain.average,
    timeStamp: DateTime.fromFormat(item.date, 'dd/MM/yyyy').toMillis(),
  }));


  const processTicks = (days: number, sourceData: DataItem[]) => {
    if (days === Infinity) {
      // Generate ticks dynamically from data range
      const start = DateTime.fromFormat(sourceData[0]?.date, 'dd/MM/yyyy').startOf('month');
      const end = DateTime.now().endOf('month');
      const ticks: number[] = [];
      let current = start;
  
      while (current <= end) {
        ticks.push(current.toMillis());
        current = current.plus({ months: 1 });
      }
      return ticks;
    }

    if (days === 7) {
      return Array.from({ length: 7 }).map((_, i) =>
        DateTime.now().minus({ days: 7 - i - 1 }).startOf('day').toMillis()
      );
    } else if (days === 30) {
      return Array.from({ length: 31 }).map((_, i) =>
        DateTime.now().minus({ days: 31 - i - 1 }).startOf('day').toMillis()
      );
    } else if (days === 90) {
      return Array.from({ length: 13 }).map((_, i) =>
        DateTime.now().minus({ weeks: 13 - i - 1 }).startOf('week').toMillis()
      );
    }
  
    return [];
  };

  const [domainStart, setDomainStart] = useState<number>(0);
  const [domainEnd, setDomainEnd] = useState<number>(0);
  
  const handleTimeRangeChange = (days: number) => {
    if (days === Infinity) {
      const firstDate = userData.length
        ? DateTime.fromFormat(userData[0].date, 'dd/MM/yyyy')
        : DateTime.now();
      const lastDate = DateTime.now();
      setFilteredData(userData); // Show all data
      setDaysShown(days);
      setDomainStart(firstDate.startOf('day').toMillis());
      setDomainEnd(lastDate.endOf('day').toMillis());
    } else {
      setDaysShown(days);
    }
  };

  const domainStart_m =
    daysShown === Infinity
    ? filteredData.length
      ? DateTime.fromFormat(filteredData[0]?.date, 'dd/MM/yyyy').toMillis()
      : DateTime.now().toMillis()
    : DateTime.now().minus({ days: daysShown }).startOf('day').toMillis();

  const domainEnd_m = DateTime.now().endOf('day').toMillis();
  const ticks = processTicks(daysShown, useGroupData ? groupData : userData);

  return (
    <div className="space-y-4">
      {/* ── Header row ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-800">
          Trends — <span className="text-MyovoltPurple">{useGroupData ? groupId : userName}</span>
        </h2>
        <div className="flex items-center gap-3">
          {groupId && (
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <span>User</span>
              <input
                type="checkbox"
                className="toggle toggle-xs toggle-primary"
                checked={useGroupData}
                onChange={() => setUseGroupData(!useGroupData)}
              />
              <span>Group</span>
            </label>
          )}
          <button
            className="btn btn-xs btn-outline"
            onClick={() => useGroupData
              ? exportMetricsToCsv("Group Metrics.csv", groupData as any[], groupId!)
              : exportMetricsToCsv("User Metrics.csv", userData as any[], userEmail!)
            }
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* ── Chart ── */}
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={transformedData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
          <Tooltip content={<CustomTooltip />} />
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            interval={0}
            ticks={ticks}
            tickFormatter={formatTick}
            dataKey="timeStamp"
            type="number"
            domain={[domainStart_m, domainEnd_m]}
            scale="time"
            style={{ fontSize: '11px' }}
          />
          <YAxis
            domain={[0, 10]}
            style={{ fontSize: '11px' }}
            ticks={[0, 5, 10]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6b7280' }}
          />
          <YAxis
            yAxisId="t"
            domain={useGroupData ? [0, 1000] : [0, 100]}
            ticks={[]}
            axisLine={false}
            tickLine={false}
            tick={false}
          />
          {lineVisibility[0] && <Bar yAxisId="t" dataKey="treatmentTotal" barSize={6} fill="#9747ff" isAnimationActive={false} radius={[2, 2, 0, 0]} />}
          {lineVisibility[1] && (
            <>
              <Area type="monotone" dataKey="mobilityAverage" stroke="#56c1ca" fill="rgba(86,193,202,0.08)" dot={{ r: 0 }} isAnimationActive={false} />
              <Line type="monotone" strokeWidth={2} dataKey="mobilityAverage" stroke="#56c1ca" dot={{ r: 0 }} isAnimationActive={false} />
            </>
          )}
          {lineVisibility[2] && (
            <>
              <Area type="monotone" dataKey="recovery" stroke="#73E38C" fill="rgba(115,227,140,0.08)" dot={{ r: 0 }} isAnimationActive={false} />
              <Line type="monotone" strokeWidth={2} dataKey="recovery" stroke="#73E38C" dot={{ r: 0 }} isAnimationActive={false} />
            </>
          )}
          {lineVisibility[3] && (
            <>
              <Area type="monotone" dataKey="painAverage" stroke="#F60E38" fill="rgba(246,14,56,0.08)" dot={{ r: 0 }} isAnimationActive={false} />
              <Line type="monotone" strokeWidth={2} dataKey="painAverage" stroke="#F60E38" dot={{ r: 0 }} isAnimationActive={false} />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* ── Stat readouts ── */}
      {readoutData && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Treatment', value: `${readoutData.treatment.hours}h ${readoutData.treatment.minutes}m`, color: '#9747ff', trend: null },
            { label: 'Mobility', value: String(readoutData.mobility.value), color: '#56c1ca', trend: readoutData.mobility.trend, higherIsBetter: true },
            { label: 'Pain', value: String(readoutData.pain.value), color: '#F60E38', trend: readoutData.pain.trend, higherIsBetter: false },
            { label: 'Recovery', value: String(readoutData.recovery.value), color: '#73E38C', trend: readoutData.recovery.trend, higherIsBetter: true },
          ].map(({ label, value, color, trend, higherIsBetter }) => (
            <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="text-lg font-semibold" style={{ color }}>{value}</p>
              {trend != null && trend !== 0 && (
                <p className={`text-xs mt-0.5 ${(higherIsBetter ? trend > 0 : trend < 0) ? 'text-green-500' : 'text-red-500'}`}>
                  {trend > 0 ? '▲' : '▼'} {Math.abs(trend)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Time range buttons ── */}
      <div className="flex flex-wrap gap-2">
        {timeRanges.map((range) => (
          <button
            key={range.value}
            className={`btn btn-sm ${daysShown === range.value ? 'btn-primary' : 'btn-ghost border border-gray-200'}`}
            onClick={() => handleTimeRangeChange(range.value)}
          >
            {range.label}
          </button>
        ))}
      </div>

      {/* ── Series toggles ── */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: 'Treatment', color: '#9747ff', idx: 0 },
          { label: 'Mobility',  color: '#56c1ca', idx: 1 },
          { label: 'Recovery',  color: '#73E38C', idx: 2 },
          { label: 'Pain',      color: '#F60E38', idx: 3 },
        ].map(({ label, color, idx }) => (
          <button
            key={label}
            className="btn btn-xs"
            style={{ color, borderColor: color, backgroundColor: 'white', opacity: lineVisibility[idx] ? 1 : 0.3 }}
            onClick={() => toggleLineVisibility(idx)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default MetricsGraph;